import { gql } from '../../core/graphql-client.js';
import { router } from '../../core/router.js';
import { toast } from '../../core/event-bus.js';
import type { Quarter, AvailabilityStats, Schedule } from '../../types.js';

export class DashboardPage extends HTMLElement {
  connectedCallback(): void {
    this.innerHTML = '<loading-spinner text="Ładowanie panelu..."></loading-spinner>';
    this.loadData();
  }

  private async loadData(): Promise<void> {
    try {
      const qData = await gql.query<{ quarters: Quarter[] }>(`
        query { quarters { year quarterNum label isActive } }
      `);
      const quarter = qData.quarters.find(q => q.isActive);

      if (!quarter) {
        this.renderNoQuarter();
        return;
      }

      const [matrixData, draftData] = await Promise.all([
        gql.query<{ availabilityMatrix: { stats: AvailabilityStats } }>(`
          query($year: Int!, $qn: Int!) {
            availabilityMatrix(year: $year, quarterNum: $qn) {
              stats { total responded pending pendingPeople }
            }
          }
        `, { year: quarter.year, qn: quarter.quarterNum }),
        gql.query<{ schedule: Schedule | null }>(`
          query($year: Int!, $qn: Int!) {
            schedule(year: $year, quarterNum: $qn) { status generatedAt publishedAt }
          }
        `, { year: quarter.year, qn: quarter.quarterNum }),
      ]);

      this.renderDashboard(quarter, matrixData.availabilityMatrix.stats, draftData.schedule);
    } catch (e) {
      this.innerHTML = `<div class="page"><p class="error-text">Błąd: ${e instanceof Error ? e.message : e}</p></div>`;
    }
  }

  private renderNoQuarter(): void {
    this.innerHTML = `
      <div class="page">
        <div class="page-header"><h1>Panel administracyjny</h1></div>
        <div class="card">
          <p>Brak aktywnego kwartału. Utwórz kwartał, aby rozpocząć pracę.</p>
          <button class="btn btn-primary" id="go-quarters">Przejdź do kwartałów</button>
        </div>
      </div>
    `;
    this.querySelector('#go-quarters')!.addEventListener('click', () => router.navigate('/admin/quarters'));
  }

  private renderDashboard(quarter: Quarter, stats: AvailabilityStats, draft: Schedule | null): void {
    const pct = stats.total > 0 ? Math.round((stats.responded / stats.total) * 100) : 0;

    this.innerHTML = `
      <div class="page">
        <div class="page-header"><h1>Panel — ${quarter.label}</h1></div>

        <div class="dashboard-grid">
          <div class="card stat-card">
            <div class="stat-value">${stats.responded}/${stats.total}</div>
            <div class="stat-label">Odpowiedzi na dyspozycyjność (${pct}%)</div>
            <div class="stat-bar">
              <div class="stat-bar-fill" style="width:${pct}%"></div>
            </div>
            ${stats.pending > 0 ? `
              <div class="stat-pending">
                <span class="stat-pending-label">Czekamy na:</span>
                ${stats.pendingPeople.map(name => `<span class="chip chip-small">${name}</span>`).join('')}
              </div>
              <button class="btn btn-primary btn-sm" id="send-reminder">📨 Wyślij przypomnienie</button>
            ` : '<p class="stat-ok">Wszyscy odpowiedzieli!</p>'}
          </div>

          <div class="card stat-card">
            <div class="stat-value">${draft ? (draft.status === 'PUBLISHED' ? 'Opublikowany' : 'Szkic') : 'Brak'}</div>
            <div class="stat-label">Grafik</div>
            ${draft?.publishedAt ? `<div class="stat-detail">Opublikowany: ${new Date(draft.publishedAt).toLocaleString('pl-PL')}</div>` : ''}
            ${draft?.generatedAt && !draft?.publishedAt ? `<div class="stat-detail">Wygenerowany: ${new Date(draft.generatedAt).toLocaleString('pl-PL')}</div>` : ''}
          </div>
        </div>

        <h2 class="section-title">Szybkie akcje</h2>
        <div class="action-grid">
          <button class="btn btn-secondary action-btn" id="go-people">👥 Osoby</button>
          <button class="btn btn-secondary action-btn" id="go-quarters">📆 Kwartały</button>
          <button class="btn btn-secondary action-btn" id="go-schedule">📝 Grafik</button>
          <button class="btn btn-secondary action-btn" id="go-matrix">🔲 Macierz</button>
          <button class="btn btn-secondary action-btn" id="prepare-quarter">📋 Przygotuj następny kwartał</button>
        </div>
      </div>
      <style>
        .dashboard-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: var(--space-md); margin-bottom: var(--space-lg); }
        .stat-card { display: flex; flex-direction: column; gap: var(--space-sm); }
        .stat-value { font-size: 2rem; font-weight: 700; color: var(--color-primary); }
        .stat-label { font-size: .875rem; color: var(--color-on-surface-muted); }
        .stat-bar { height: 6px; background: var(--color-surface-container); border-radius: 3px; overflow: hidden; }
        .stat-bar-fill { height: 100%; background: var(--color-primary); border-radius: 3px; transition: width .3s; }
        .stat-pending { display: flex; flex-wrap: wrap; gap: var(--space-xs); align-items: center; margin-top: var(--space-xs); }
        .stat-pending-label { font-size: .8rem; color: var(--color-on-surface-muted); }
        .chip-small { font-size: .75rem; padding: 2px 8px; }
        .stat-ok { font-size: .875rem; color: var(--color-success); font-weight: 500; }
        .stat-detail { font-size: .8rem; color: var(--color-on-surface-muted); }
        .section-title { font-size: 1.125rem; margin-bottom: var(--space-md); }
        .action-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)); gap: var(--space-sm); }
        .action-btn { padding: var(--space-md); font-size: .9rem; }
        .error-text { color: var(--color-error); }
      </style>
    `;

    this.querySelector('#go-people')!.addEventListener('click', () => router.navigate('/admin/people'));
    this.querySelector('#go-quarters')!.addEventListener('click', () => router.navigate('/admin/quarters'));
    this.querySelector('#go-schedule')!.addEventListener('click', () => router.navigate('/admin/schedule'));
    this.querySelector('#go-matrix')!.addEventListener('click', () => router.navigate('/admin/matrix'));

    this.querySelector('#prepare-quarter')!.addEventListener('click', async (e) => {
      const btn = e.target as HTMLButtonElement;
      btn.disabled = true;
      btn.textContent = 'Przygotowuję...';
      try {
        const data = await gql.query<{ prepareNextQuarter: { created: boolean; label: string; saturdaysCount: number; remindersSent: number } }>(`
          mutation { prepareNextQuarter { created label saturdaysCount remindersSent } }
        `);
        const r = data.prepareNextQuarter;
        if (r.created) {
          toast(`Utworzono kwartał ${r.label} (${r.saturdaysCount} sobót). Wysłano ${r.remindersSent} przypomnień.`, 'success');
        } else {
          toast(`Kwartał ${r.label} już istnieje. Wysłano ${r.remindersSent} przypomnień.`, 'info');
        }
        this.loadData(); // refresh dashboard
      } catch (err) {
        toast(`Błąd: ${err instanceof Error ? err.message : err}`, 'error');
      }
      btn.disabled = false;
      btn.textContent = '📋 Przygotuj następny kwartał';
    });

    this.querySelector('#send-reminder')?.addEventListener('click', async (e) => {
      const btn = e.target as HTMLButtonElement;
      btn.disabled = true;
      btn.textContent = 'Wysyłam...';
      try {
        const data = await gql.query<{ sendAvailabilityReminder: { sent: number; skipped: number } }>(`
          mutation { sendAvailabilityReminder { sent skipped } }
        `);
        const { sent, skipped } = data.sendAvailabilityReminder;
        toast(`Wysłano ${sent} przypomnień${skipped > 0 ? `, pominięto ${skipped} (brak kontaktu)` : ''}`, 'success');
      } catch (err) {
        toast(`Błąd wysyłki: ${err instanceof Error ? err.message : err}`, 'error');
      }
      btn.disabled = false;
      btn.textContent = '📨 Wyślij przypomnienie';
    });
  }
}

customElements.define('admin-dashboard-page', DashboardPage);
